import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { sendContactMessage, getPublicSiteSettings } from '../services/api';
import './Contact.css';

const Contact = () => {
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicSiteSettings()
      .then((res) => {
        if (res && res.data && typeof res.data === 'object') {
          setSiteSettings(res.data);
        }
      })
      .catch(() => setSiteSettings({}))
      .finally(() => setLoading(false));
  }, []);

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await sendContactMessage(formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          sujet: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'envoi du message. Veuillez réessayer.');
    }
  };

  const name = siteSettings?.site_name || 'MUTU NDE ESPOIR ASBL';
  const logo = siteSettings?.site_logo || '/logo.png';
  const address = siteSettings?.site_address || 'Kisangani, République Démocratique du Congo';
  const email = siteSettings?.site_email || 'contact@mutundeespoir.org';
  const phone = siteSettings?.site_phone || '+243 XXX XXX XXX';
  const contactTitle = siteSettings?.contact_title ?? 'Contactez-Nous';
  const contactSubtitle = siteSettings?.contact_subtitle ?? 'Nous sommes là pour vous aider';
  const contactInfoSectionTitle = siteSettings?.contact_info_section_title ?? 'Informations de Contact';
  const contactAddressLabel = siteSettings?.contact_address_label ?? 'Adresse';
  const contactEmailLabel = siteSettings?.contact_email_label ?? 'Email';
  const contactPhoneLabel = siteSettings?.contact_phone_label ?? 'Téléphone';
  const contactHoursLabel = siteSettings?.contact_hours_label ?? "Heures d'Ouverture";
  const contactHoursText = siteSettings?.contact_hours_text ?? 'Lundi - Vendredi: 8h00 - 17h00\nSamedi: 9h00 - 13h00\nDimanche: Fermé';
  const contactSocialTitle = siteSettings?.contact_social_title ?? 'Suivez-Nous';
  const contactSocialLinks = Array.isArray(siteSettings?.contact_social_links) ? siteSettings.contact_social_links : [{ name: 'Facebook', url: 'https://facebook.com', icon: '📘' }, { name: 'Twitter', url: 'https://twitter.com', icon: '🐦' }, { name: 'LinkedIn', url: 'https://linkedin.com', icon: '💼' }];
  const contactFormTitle = siteSettings?.contact_form_title ?? 'Envoyez-Nous un Message';
  const contactSuccessTitle = siteSettings?.contact_success_title ?? 'Message Envoyé !';
  const contactSuccessText = siteSettings?.contact_success_text ?? 'Nous vous répondrons dans les plus brefs délais.';

  if (loading) {
    return (
      <div className="contact-page">
        <Navigation siteName={name} siteLogo={logo} />
        <div className="page-header">
          <div className="container">
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
              <p>Chargement...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <Navigation siteName={name} siteLogo={logo} />
      
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">{contactTitle}</h1>
          <p className="page-subtitle">{contactSubtitle}</p>
        </div>
      </div>

      <div className="container">
        <div className="contact-wrapper">
          <div className="contact-info-section">
            <h2>{contactInfoSectionTitle}</h2>
            
            <div className="contact-info-card">
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div>
                  <h3>{contactAddressLabel}</h3>
                  <p>{address || '—'}</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div>
                  <h3>{contactEmailLabel}</h3>
                  <p>{email || '—'}</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">📱</div>
                <div>
                  <h3>{contactPhoneLabel}</h3>
                  <p>{phone || '—'}</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">🕒</div>
                <div>
                  <h3>{contactHoursLabel}</h3>
                  {(contactHoursText || '').split('\n').filter(Boolean).map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                  {!contactHoursText && <p>—</p>}
                </div>
              </div>
            </div>

            <div className="social-section">
              <h3>{contactSocialTitle}</h3>
              <div className="social-links">
                {contactSocialLinks.filter(l => l && (l.url || l.name)).map((link, i) => (
                  <a key={i} href={link.url || '#'} target="_blank" rel="noopener noreferrer" className="social-link" aria-label={link.name}>
                    <span>{link.icon || '🔗'}</span> {link.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="contact-form-section">
            <h2>{contactFormTitle}</h2>
            
            {submitted ? (
              <div className="success-message">
                <div className="success-icon">✅</div>
                <h3>{contactSuccessTitle}</h3>
                <p>{contactSuccessText}</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="nom">Nom Complet *</label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="Votre nom complet"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="votre.email@exemple.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="telephone">Téléphone</label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="+243 XXX XXX XXX"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sujet">Sujet *</label>
                  <select
                    id="sujet"
                    name="sujet"
                    value={formData.sujet}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="adhesion">Adhésion</option>
                    <option value="services">Services</option>
                    <option value="reclamation">Réclamation</option>
                    <option value="information">Information</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Votre message..."
                  ></textarea>
                </div>

                <button type="submit" className="submit-btn">
                  Envoyer le Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="presentation-footer">
        <div className="container">
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} {name}. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;

